import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translateService = inject(TranslateService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Extract error message from backend response
      let errorMessage = 'errors.unknown';

      if (error.error && typeof error.error === 'object') {
        // Backend error with message field
        if (error.error.message) {
          errorMessage = error.error.message;
        }
        // Handle validation errors with field-specific messages
        else if (error.error.errors && Array.isArray(error.error.errors)) {
          // Multiple validation errors
          error.error.errors.forEach((err: any) => {
            const fieldError = err.field ? `${err.error} (${err.field})` : err.error || err.message;

            showErrorToast(messageService, translateService, fieldError);
          });

          // Don't show additional toast
          return throwError(() => error);
        }
      } else if (error.message) {
        // Network error or other client-side error
        errorMessage = error.message.includes('Http failure') ? 'errors.network' : error.message;
      }

      // Set default error message based on status code if no specific message
      if (errorMessage === 'errors.unknown') {
        if (error.status >= 500) {
          errorMessage = 'errors.server';
        } else if (error.status === 401) {
          errorMessage = 'errors.unauthorized';
        } else if (error.status === 403) {
          errorMessage = 'errors.forbidden';
        }
      }

      // Show toast notification (always with 'error' severity)
      showErrorToast(messageService, translateService, errorMessage);

      // Re-throw error for component-level handling if needed
      return throwError(() => error);
    })
  );
};

/**
 * Helper function to show error toast
 * Translates the error message and displays it using MessageService
 */
function showErrorToast(
  messageService: MessageService,
  translateService: TranslateService,
  errorKey: string
): void {
  // If the error key starts with 'validation.', prefix it with 'errors.' for translation lookup
  let translationKey = errorKey;
  if (errorKey.startsWith('validation.')) {
    translationKey = `errors.${errorKey}`;
  }
  // Translate the error message
  const translatedMessage = translateService.instant(translationKey);

  // Fallback to generic error if translation not found
  const message =
    translatedMessage === translationKey
      ? translateService.instant('errors.unknown')
      : translatedMessage;

  // Always use 'error' severity and title
  const summary = translateService.instant('errors.title');

  // Show toast notification
  messageService.add({
    severity: 'error',
    summary,
    detail: message,
    life: 5000, // 5 seconds
    closable: true,
  });
}
